"use client";
import dynamic from "next/dynamic";
import PropTypes from "prop-types";
import isEmpty from "lodash.isempty";
import React, { useState, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  setClientCurrentClickedItem,
  setClientVariantsLabel,
  setClientVariantType,
} from "../../../store/slices/pdpSlice";
import IconButtonComponent from "../../atoms/iconButton";
import { CAKE_TYPE, constants, labelStyle } from "../../../constants/pageConstants/pdpConstants";
import Typography from "../../atoms/typography";
import CustomImage from "../../atoms/customImage";
import { calDiscountPercentage, debounce, getProductDeliveryType, updateCurrency } from "../../../utils/common";
import useAnalytics from "../../../analytics/useAnalytics";
import { cleverTapCommonEvents, cleverTapCommonWords } from "../../../analytics/clevertapConstants";
import { changeVariantGaEvent } from "../../../analytics/gaEvents";
import { GA4_EVENTS } from "../../../analytics/gaConstant";
import { COUNTRIES, CURRENCY_ID } from "../../../constants/common/common";
import {
  requestToFetchDeliveryDatesAction,
  requestToFetchInternationalShippingMethod,
  setDeliveryDetailsAction,
} from "../../../store/slices/common/shippingDateAndSlotSelection";

const ProductPreview = dynamic(() => import("../ProductPreview"), { ssr: false });
const Drawer = dynamic(() => import("../drawer"), { ssr: false });

const FlavorTypes = dynamic(() => import("../flavorTypes"), { ssr: false });

/**
 * Variants Components  for rendering variant selection and product display.
 *
 * @returns {React.Component} The Variants Components .
 */
const VariantsComponents = ({ isMobile }) => {
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();

  const imageDomain = process.env.NEXT_PUBLIC_IMAGE_DOMAIN;

  const {
    clientVariants,
    clientVariantLabel,
    clientVariantType,
    flavorList,
    clientVariantFlavour,
    selectedCurrency,
    catalogId,
    productId,
    pincode,
    fullProductDetails,
    checkLoginStatus,
    selectedDomesticLocation,
    selectedIntLocation,
  } = useSelector(
    (state) => ({
      clientVariants: state.pdp.clientVariants,
      clientVariantLabel: state.pdp.clientVariantLabel,
      clientVariantType: state.pdp.clientVariantType,
      flavorList: state.pdp.flavorList,
      clientVariantFlavour: state.pdp.clientVariantFlavour,
      selectedCurrency: state.currencyList?.userSelectedCurrency,
      catalogId: state.appConfig.catalogId,
      productId: state?.pdp?.fullProductDetails?.productId,
      pincode: state?.locationLock?.selectedDomesticLocation?.pincode,
      fullProductDetails: state?.pdp?.fullProductDetails,
      checkLoginStatus: state.fus.loginDetail?.loggedin,
      selectedDomesticLocation: state?.locationLock?.selectedDomesticLocation,
      selectedIntLocation: state?.locationLock?.selectedIntLocation,
    }),
    shallowEqual,
  );

  const [editCombo, setEditCombo] = useState([]);
  const discountPercentage = calDiscountPercentage(
    fullProductDetails?.productPrice?.listPrice,
    fullProductDetails?.productPrice?.price,
  );
  const productDeliveryType = getProductDeliveryType(fullProductDetails?.primaryCategoryId);
  const commonCleverTapData = {
    page_type: cleverTapCommonWords.PDP,
    page_name: fullProductDetails?.urlIdentifier,
    product_name: fullProductDetails?.productName,
    pid: fullProductDetails?.productId,
    product_category: fullProductDetails?.primaryCategoryId,
    is_variant_available: fullProductDetails?.isVariant === "Y",
    delivery_type: productDeliveryType,
    product_price: fullProductDetails?.productPrice?.price,
    currency: selectedCurrency?.currency?.id ?? "INR",
    strike_price: fullProductDetails?.productPrice?.listPrice,
    discount_percentage: discountPercentage,
    product_rating: fullProductDetails?.productReviewsAndRatings?.ratingValue,
    product_review_count: fullProductDetails?.productReviewsAndRatings?.totalReview,
    is_product_available: fullProductDetails?.stockAvailable,
    is_international: catalogId !== COUNTRIES.INDIA,
    city: catalogId !== COUNTRIES.INDIA ? selectedIntLocation?.location : "",
    area: catalogId !== COUNTRIES.INDIA ? "" : selectedDomesticLocation?.address,
    pincode: catalogId !== COUNTRIES.INDIA ? "" : pincode,
    system_pincode: catalogId !== COUNTRIES.INDIA ? "" : pincode,
    country: catalogId,
    sku_id: fullProductDetails?.skuCode,
    login_status: checkLoginStatus,
  };

  /**
   * Handles the click event for selecting a variant option.
   *
   * @param {Object} parentItem - The parent item containing the variant options.
   * @param {string} item - The selected variant type.
   */
  const onClickVariantOptions = useMemo(() => {
    return debounce((parentItem, item) => {
      dispatch(setClientVariantType(item));

      if (flavorList.length) {
        dispatch(setClientCurrentClickedItem(parentItem[clientVariantLabel][item]?.[clientVariantFlavour]));
      } else {
        dispatch(setClientCurrentClickedItem(parentItem[clientVariantLabel][item]));
      }

      // Track event

      trackEvent(
        cleverTapCommonEvents.variantSelected,
        { ...commonCleverTapData, is_egg: item?.toUpperCase() === CAKE_TYPE.WITH_EGG ?? false },
        ["clevertap"],
      );
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flavorList, clientVariantLabel, clientVariantFlavour, commonCleverTapData]);

  /**
   * Handles the click event for selecting a product image.
   *
   * @param {string} itemKey - The key of the selected variant.
   * @param {Object} extractData - The data extracted from the selected variant.
   */
  const onClickProductImages = useMemo(() => {
    return debounce((itemKey, extractData) => {
      dispatch(setClientVariantsLabel(itemKey));
      dispatch(setClientCurrentClickedItem(extractData));

      const productIds = extractData?.productId;

      if (selectedDomesticLocation?.address && pincode && catalogId === COUNTRIES.INDIA) {
        dispatch(
          requestToFetchDeliveryDatesAction({
            productId: productIds,
            pinCode: pincode,
            currencyUomId: CURRENCY_ID,
            catalogId: extractData?.catalogId,
            countryGeoId: extractData?.countryGeoId,
          }),
        );
      } else if (selectedIntLocation?.pincode && catalogId !== COUNTRIES.INDIA) {
        dispatch(
          requestToFetchInternationalShippingMethod({
            productId: productIds,
            city: selectedIntLocation?.pincode,
          }),
        );
      }

      dispatch(setDeliveryDetailsAction({}));

      // Track events
      trackEvent(GA4_EVENTS?.VARIANT_CHANGED, { ...changeVariantGaEvent(extractData, pincode, productId) }, ["ga4"]);
      trackEvent(cleverTapCommonEvents.variantSelected, { ...commonCleverTapData, is_egg: "" }, ["clevertap"]);
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDomesticLocation, pincode, catalogId, selectedIntLocation, productId, commonCleverTapData]);

  /**
   * Checks if the specified category type is present in the given item.
   *
   * @param {Object} item - The item object to check.
   * @returns {boolean} - Returns `true` if the category type is present, otherwise `false`.
   */
  const checkItsCategoryPresent = (item) => {
    const itemKey = Object.keys(item)?.[0];

    let extractData = item[itemKey][clientVariantType];
    return extractData ? true : false;
  };

  /**
   * Handles the edit button click event by setting the value for the combo being edited.
   *
   * @param {any} value - The value to set for editing the combo.
   * @returns {void}
   */
  const onHandleEditClick = (value) => {
    setEditCombo(value);
  };

  /**
   * Extracts specific data from the given item based on the provided keys and conditions.
   *
   * @param {Object} item - The item object containing data to extract.
   * @param {string} itemKey - The key to access the relevant data in the item object.
   * @returns {any} - The extracted data based on `flavorList`, `clientVariantType`, or fallback to `itemKey`.
   */
  const retrieveItemVariantData = (item, itemKey) => {
    if (flavorList?.length) {
      return item[itemKey][clientVariantType]?.[clientVariantFlavour];
    }
    if (!isEmpty(clientVariantType)) {
      return item?.[itemKey]?.[clientVariantType];
    }
    return item[itemKey];
  };
  return (
    <>
      {clientVariants?.map((parentItem, pIndex) => {
        if (checkItsCategoryPresent(parentItem) && parentItem[clientVariantLabel]) {
          const categoryValue = Object.keys(parentItem[clientVariantLabel]);
          return (
            <div key={pIndex} className="flex gap-10">
              {categoryValue?.map((item, index) => {
                return (
                  <div key={index} className="pb-5" onClick={() => onClickVariantOptions(parentItem, item)}>
                    <IconButtonComponent
                      labelColor="w-full font-500"
                      className={`rounded-full p-3 py-2 ${item == clientVariantType ? "border-1 border-darkOliveGreen bg-fnp-50" : "border border-gray-300 text-grey-400"}`}
                      buttonVarient="iconWithText"
                      label={item}
                      imgWidth={40}
                      imgHeight={40}
                      iconSrc={item.toUpperCase() == CAKE_TYPE.EGG_LESS ? "/icons/veg.svg" : "/icons/egg.svg"}
                      altText={`${item} icon`}
                      classes={`flex items-center p-3 md:p-4`}
                    />
                  </div>
                );
              })}
            </div>
          );
        }
      })}
      <div className="flex flex-col gap-4">
        {!isEmpty(clientVariants) && (
          <>
            <Typography variant="p" Tag="p" classes={labelStyle}>
              {constants.AVAILABLE_COMBOS}
            </Typography>

            <div className="flex gap-10 overflow-y-hidden scrollbar-thin scrollbar-webkit xxl:gap-12" id="pdpVariants">
              {clientVariants?.map((item) => {
                const itemKey = Object.keys(item)?.[0];
                let extractData = retrieveItemVariantData(item, itemKey);
                const imageURL = extractData?.productContent?.productImages?.[0];

                if (extractData) {
                  return (
                    <div
                      onClick={() => onClickProductImages(itemKey, extractData)}
                      key={imageURL}
                      className={`cursor-pointer rounded-[10px] border ${clientVariantLabel == itemKey ? "border-darkOliveGreen bg-fnp-50" : "border-gray-300"} mb-4 w-[25%] min-w-[100px] p-3 duration-300 ease-in-out hover:border-darkOliveGreen hover:bg-fnp-50 md:min-w-[120px] md:p-[5px] xmll:min-w-[136px] xxl:min-w-[148px] xxl:p-[5px]`}
                    >
                      <CustomImage
                        src={(imageDomain + imageURL?.slice(1, imageURL?.length))?.replace("/l/", "/s/")}
                        width={100}
                        height={100}
                        isPriority={false}
                        alt={"image"}
                        borderRadius={10}
                        sizes="10vw"
                        customStyle={{ width: "100%", height: "65% xxl:h-[70%]" }}
                      />
                      <div className="pt-4 text-gray-500 xxl:pb-8">
                        <div className="flex justify-between">
                          <div
                            className={`text-10 xxl:text-12 ${clientVariantLabel == itemKey ? "font-500 text-fnp-400" : "font-400 text-grey-500"}`}
                          >
                            {itemKey}
                          </div>
                          {!isEmpty(extractData?.productComboComponents) && (
                            <CustomImage
                              onClick={() => onHandleEditClick(extractData?.productComboComponents)}
                              src={"/icons/edit.svg"}
                              width={13}
                              height={13}
                              isPriority={false}
                              alt={"image"}
                              borderRadius={4.8}
                              sizes="10vw"
                            />
                          )}
                        </div>

                        <div
                          className={`pt-4 text-14 xxl:text-16 ${clientVariantLabel == itemKey ? "font-500 text-fnp-400" : "font-400 text-grey-500"}`}
                        >
                          {updateCurrency(selectedCurrency, extractData?.productPrice?.price)}
                        </div>
                      </div>
                    </div>
                  );
                }
              })}
            </div>
          </>
        )}
      </div>

      <Drawer
        styles={"pt-0 pb-16"}
        position={isMobile ? "bottom" : "right"}
        isOpen={!isEmpty(editCombo)}
        onDrawerClose={() => onHandleEditClick([])}
      >
        <div className="bg-white flex flex-col justify-between border-gray-200">
          <Typography
            Tag="p"
            classes="w-full pl-8 flex items-center flex-shrink-0 font-600 text-16 text-fnp-500 border-b py-14"
          >
            {constants.MODIFY_YOUR_ORDER}
          </Typography>
          <>
            {editCombo?.map((item, index) => {
              return (
                <>
                  <ProductPreview
                    key={item?.productId}
                    productImage={imageDomain + item?.productImages?.[0]}
                    productName={item?.productName}
                    price={item?.productPrice.price}
                    listPrice={item.productPrice.listPrice}
                    redirectedURL={item?.productUrl}
                  />
                  {index < editCombo?.length - 1 && <div className="border-b" />}
                </>
              );
            })}
          </>
        </div>
      </Drawer>
      {!isEmpty(flavorList) && (
        <div className="px-0 pt-20">
          <FlavorTypes flavorList={flavorList} isMobile={isMobile} />
        </div>
      )}
    </>
  );
};

export default React.memo(VariantsComponents);

VariantsComponents.propTypes = { isMobile: PropTypes.bool };
